/**
 * VA-27998 — the screen-share hold.
 *
 * A candidate who stops sharing their screen puts the interview into a HOLD: a
 * blocking overlay asks them to share again, the clock freezes, and if the
 * countdown runs out the interview is terminated.
 *
 * THIS IS NOT A RECONNECT, and keeping the two apart is the whole reason this
 * lives in its own module rather than being folded into the connectivity code.
 *
 *   network reconnect          screen-share hold
 *   ─────────────────          ─────────────────
 *   socket is GONE             socket is healthy and untouched
 *   session being rebuilt      session alive
 *   "connection lost"          "screen sharing required"
 *   ~2 min budget              30 s budget
 *   ends in RESCHEDULE         ends in TERMINATION
 *
 * So this path must never show connection-lost wording, never tear down or
 * rebuild the socket, and never enter the reconnect state machine — that flow
 * ends in a reschedule, the opposite of the decision here. The only thing the
 * two share is the backend timer primitive, which the server holds under its
 * own `screen_share` reason so they stay separable in logs and metrics.
 *
 * Lives here, once, so both modes get identical behaviour: shared core,
 * per-mode edges. Each context wires it explicitly rather than inheriting it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
/** How long the candidate has to restore sharing before the interview ends. */
const DEFAULT_HOLD_SECONDS = 30;
/**
 * The countdown, in seconds.
 *
 * 30 s and termination-on-expiry are a product decision, not a default anyone
 * should tune away casually — but the value is config-backed rather than a
 * literal so an environment can be adjusted without a deploy.
 */
export const screenShareHoldSeconds = () => {
    const raw = Number(process.env.REACT_APP_SCREEN_SHARE_HOLD_SECONDS);
    return Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : DEFAULT_HOLD_SECONDS;
};
/** How often the countdown re-renders. */
const TICK_MS = 250;
export const SCREEN_SHARE_MESSAGES = {
    stopped: 'screen_share_stopped',
    resumed: 'screen_share_resumed',
    terminate: 'screen_share_terminate',
};
export const useScreenShareHold = ({ send, onExpire, isSuspended, messages = SCREEN_SHARE_MESSAGES, holdSeconds, }) => {
    const totalSeconds = holdSeconds !== null && holdSeconds !== void 0 ? holdSeconds : screenShareHoldSeconds();
    const msgRef = useRef(messages);
    msgRef.current = messages;
    const [held, setHeld] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(null);
    // Read through refs inside the interval so the ticker never has to be torn
    // down and rebuilt when a caller passes a new closure — a countdown that
    // restarts on re-render would silently give the candidate extra time.
    const sendRef = useRef(send);
    sendRef.current = send;
    const onExpireRef = useRef(onExpire);
    onExpireRef.current = onExpire;
    const isSuspendedRef = useRef(isSuspended);
    isSuspendedRef.current = isSuspended;
    // The deadline is a REF, not state, and that is load-bearing rather than a
    // style choice. Suspending re-bases it from inside the ticker; if that went
    // through state, the running interval would keep its stale closure until
    // React re-rendered, and the very next tick would compare `now` against a
    // deadline that expired during the outage — terminating the candidate the
    // instant their connection came back, which is precisely the outcome H0
    // exists to prevent.
    const deadlineRef = useRef(null);
    /** Milliseconds still owed, captured when the countdown was suspended. */
    const suspendedRemainingRef = useRef(null);
    // Latches so expiry can fire exactly once even if a tick overlaps a re-render.
    const expiredRef = useRef(false);
    const enterHold = useCallback(() => {
        if (deadlineRef.current !== null)
            return; // already holding; may re-fire
        expiredRef.current = false;
        suspendedRemainingRef.current = null;
        deadlineRef.current = Date.now() + totalSeconds * 1000;
        setSecondsLeft(totalSeconds);
        setHeld(true);
        // Tell the server so it can freeze the clock and record the interruption.
        // Best-effort: if the socket is momentarily closed the overlay still shows
        // and still terminates, because the control must not depend on the wire.
        sendRef.current({ type: msgRef.current.stopped });
    }, [totalSeconds]);
    const exitHold = useCallback(() => {
        if (deadlineRef.current === null)
            return;
        deadlineRef.current = null;
        expiredRef.current = false;
        suspendedRemainingRef.current = null;
        setSecondsLeft(null);
        setHeld(false);
        sendRef.current({ type: msgRef.current.resumed });
    }, []);
    useEffect(() => {
        if (!held)
            return undefined;
        const tick = () => {
            var _a;
            const deadline = deadlineRef.current;
            if (deadline === null || expiredRef.current)
                return;
            // H0: hold the countdown still while the network is down. The candidate
            // cannot click "Share Screen" with no connection, so draining their
            // budget would punish an outage they cannot act on.
            if ((_a = isSuspendedRef.current) === null || _a === void 0 ? void 0 : _a.call(isSuspendedRef)) {
                if (suspendedRemainingRef.current === null) {
                    suspendedRemainingRef.current = Math.max(0, deadline - Date.now());
                }
                setSecondsLeft(Math.max(0, Math.ceil(suspendedRemainingRef.current / 1000)));
                return;
            }
            if (suspendedRemainingRef.current !== null) {
                // Back online: re-base immediately, in the ref, so this same interval
                // sees the new deadline on its very next tick.
                deadlineRef.current = Date.now() + suspendedRemainingRef.current;
                suspendedRemainingRef.current = null;
                setSecondsLeft(Math.max(0, Math.ceil((deadlineRef.current - Date.now()) / 1000)));
                return;
            }
            const remainingMs = deadline - Date.now();
            setSecondsLeft(Math.max(0, Math.ceil(remainingMs / 1000)));
            if (remainingMs > 0)
                return;
            expiredRef.current = true;
            deadlineRef.current = null;
            // Tell the server first. It is the authority on ending the interview —
            // a browser can be closed or throttled, and an integrity control that
            // depends on the client is not a control — but the client also ends
            // locally so the candidate is never left staring at a dead overlay.
            sendRef.current({ type: msgRef.current.terminate });
            onExpireRef.current();
        };
        tick();
        const id = setInterval(tick, TICK_MS);
        return () => clearInterval(id);
    }, [held]);
    const resyncHeld = useCallback(() => {
        if (deadlineRef.current === null || expiredRef.current)
            return;
        sendRef.current({ type: msgRef.current.stopped, resync: true });
    }, []);
    return {
        held,
        secondsLeft,
        totalSeconds,
        enterHold,
        exitHold,
        resyncHeld,
    };
};
